<template>
<!-- This component takes a list of items and displays them as a list
 of card views. -->
  <v-row class="mx-auto">
    <v-col cols="12">
      <!-- Section Header with Expand/Collapse Buttons -->
      <div class="d-flex align-center mb-4">
        <div class="section-header flex-grow-1">{{ sectionHeader }}</div>
        <div v-if="items && items.length > 0" class="d-flex">
          <v-btn-toggle>
            <v-btn 
              v-show="expandValue === 0" 
              @click="setPanels(0)" 
              icon="mdi-arrow-collapse" 
              size="small"
              variant="text"
              color="primary"
            />
            <v-btn 
              v-show="expandValue === 1" 
              @click="setPanels(1)" 
              icon="mdi-arrow-expand" 
              size="small"
              variant="text"
              color="primary"
            />
          </v-btn-toggle>
        </div>
      </div>
      
      <!-- Show items if they exist -->
      <template v-if="items && items.length > 0">
        <v-expansion-panels v-model="openedPanels" variant="accordion" class="mx-auto" multiple>
          <v-expansion-panel
            v-for="(item) in items" 
            :key="item.sortOrder"
            class="mb-2"
            elevation="1"
          >
            <v-expansion-panel-title class="mont-font mont-bold">
              {{ item.item }}
            </v-expansion-panel-title>
            <v-expansion-panel-text class="mont-font">
              {{ item.itemDescription }}
              
              <!-- YouTube embed if applicable -->
              <div v-if="isYouTubeLink(item.link)" class="d-flex justify-center pa-2 mt-4">
                <iframe height="250px" :width="videoWidth" :src="convertToEmbedLink(item.link)"
                  title="YouTube video player" frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowfullscreen></iframe>
              </div>
              
              <!-- Link button -->
              <div class="d-flex justify-end mt-2" v-if="item.link">
                <v-btn
                  color="primary"
                  variant="text"
                  @click="openLink(item.link)"
                  class="mt-2"
                >
                  <v-icon left>mdi-open-in-new</v-icon>
                  Open Link
                </v-btn>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
      <!-- Show fallback message if no items -->
      <v-card v-else hover outlined class="mb-2 fallback-card">
        <v-card-title class="mont-font text-center">
          <v-icon left color="grey darken-1">mdi-information</v-icon>
          {{ fallbackMessage }}
        </v-card-title>
      </v-card>
    </v-col>
  </v-row>
</template>
  
  
  <script>
  import axios from 'axios';
  export default {
    // Properties returned from data() become reactive state
    // and will be exposed on `this`.
    name: 'ItemList',
    props: {
      sectionHeader: {
        type: String,
        required: true
      },
      items: {
        type: Array,
        required: true,
        default: () => []
      }
    },
    data() {
      return {
        openedPanels: [],
        expandValue: 1 // 1 means collapsed (show expand button), 0 means expanded (show collapse button)
      };
    },
    watch: {
      openedPanels: {
        handler() {
          // Update expandValue based on the state of panels
          if (this.items && this.items.length > 0) {
            if (this.openedPanels.length === this.items.length) {
              this.expandValue = 0; // All panels are open, show collapse button
            } else if (this.openedPanels.length === 0) {
              this.expandValue = 1; // All panels are closed, show expand button
            }
          }
        },
        deep: true
      }
    },
    computed: {
      fallbackMessage() {
        switch (this.sectionHeader) {
          case 'Current Exercises':
            return 'Not yet assigned';
          case 'Content for You':
            return 'Failed to retrieve';
          case 'Your Favorites':
            return 'Favorite to view it here';
          default:
            return 'No items available';
        }
      },
      videoWidth() {
        // Return a responsive width for the video iframe
        return '100%';
      }
    },
    // Methods are functions that mutate state and trigger updates.
  // They can be bound as event handlers in templates.
  methods: {
    // Handle item access logic here
    alertMe(){
      alert("Item clicked.")
    },
    openLink(url){
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    setPanels(value) {
      // Reset the panels array
      this.openedPanels = [];
      
      // If value is 1, expand all panels
      if (value === 1) {
        for (let i = 0; i < this.items.length; i++) {
          this.openedPanels.push(i);
        }
      }
      // Otherwise (value is 0), collapse all panels (leave openedPanels empty)
      
      // Toggle the expand value
      this.expandValue = (this.expandValue === 0) ? 1 : 0;
    },
    async redirect(item){
      if(item.sourceTable === "Page"){
        try{
          axios.defaults.baseURL = process.env.VUE_APP_API_URL;
          const response = await axios.get(`/api/pagecontent/${item.contentId}`);
          var pageId = response.data.pageId;
          this.$router.push({ name: "Page", params: {pageid: pageId}, query: { contentId: item.contentId }})
      }catch(error){
          console.error(error)
        }
      }else if(item.sourceTable === "SubPage"){
        try{
          axios.defaults.baseURL = process.env.VUE_APP_API_URL;
          const response = await axios.get(`/api/subpagecontent/${item.contentId}`);
          var subpageId = response.data.subPageId;
          this.$router.push({ name: "SubPage", params: {subpageid: subpageId}, query: { contentId: item.contentId }})
      }catch(error){
          console.error(error)
        }
      }else{
        console.log("exception")
      }
    },
    isYouTubeLink(url) {
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)(\/.*)?$/;
      return youtubeRegex.test(url);
    },
    convertToEmbedLink(watchLink) {
      if (watchLink.includes('youtube.com/watch?v=')) {
        return watchLink.replace('watch?v=', 'embed/');
      }
      console.error('Invalid YouTube watch link');
      return null;
    },
  }
  };

  </script>
  
  <style scoped>
  * {
    font-family: 'Montserrat', 'sans-serif';
  }

  .section-header {
    color: #08B6AA;
    font-size: 24px;
    font-weight: 700;
    text-align: center;
  }

  .mont-font{
    color: #000000;
  }

  .mont-bold {
    font-weight: bold;
  }

  .fallback-card {
    background-color: #f5f5f5 !important;
    border: 1px dashed #9e9e9e !important;
  }

  .fallback-card .v-card-title {
    color: #616161;
    font-size: 1.1rem;
    justify-content: center;
  }

  /* Expansion panel styling */
  :deep(.v-expansion-panel) {
    border: 1px solid rgba(0, 0, 0, 0.12);
    margin-bottom: 8px;
    width: 100%;
    max-width: 100%;
    transition: box-shadow 0.3s ease;
  }

  :deep(.v-expansion-panel:hover) {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  :deep(.v-expansion-panel-title) {
    padding: 16px;
    min-height: 64px;
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 16px 16px;
  }

  /* Ensure consistent spacing */
  .v-expansion-panels {
    width: 100%;
    max-width: 100%;
  }
  </style>
